/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { useStyletron } from "baseui";
import { StatefulPopover, PLACEMENT } from "baseui/popover";
import { Button, KIND, SIZE } from "baseui/button";
import {
  StatefulTooltip,
  PLACEMENT as TOOLTIP_PLACEMENT,
} from "baseui/tooltip";

// Derived from the component so it works across Base Web versions.
type Placement = React.ComponentProps<typeof StatefulPopover>["placement"];
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { getTooltipOverrides } from "../../DesignSystem/TooltipStyles";

export interface DropdownMenuItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  /** Right-aligned hint */
  hint?: string;
  disabled?: boolean;
  /** Tooltip shown on hover/focus (uses the shared Dexter tooltip style). */
  tooltip?: string;
  /** Tooltip shown instead of `tooltip` while the item is disabled. */
  disabledReason?: string;
  /** Draw a divider above this item. */
  dividerBefore?: boolean;
  onSelect: () => void;
}

interface DropdownMenuProps {
  items: DropdownMenuItem[];
  placement?: Placement;
  minWidth?: number;
  children: React.ReactElement;
}

/**
 * Wraps a menu option in the shared Dexter tooltip. Opens to the side so it
 * doesn't cover neighbouring options; the block wrapper keeps it working on
 * disabled options, which don't fire mouse events themselves.
 */
function ItemTooltip({
  content,
  children,
}: {
  content?: string;
  children: React.ReactElement;
}): JSX.Element {
  const { currentTheme } = useDexterTheme();
  if (!content) return children;
  return (
    <StatefulTooltip
      placement={TOOLTIP_PLACEMENT.right}
      content={content}
      overrides={getTooltipOverrides(currentTheme)}
      showArrow
    >
      <div style={{ display: "block", width: "100%" }}>{children}</div>
    </StatefulTooltip>
  );
}

/**
 * Popover menu shared by the header
 */
export function DropdownMenu({
  items,
  placement = PLACEMENT.bottomRight,
  minWidth = 200,
  children,
}: DropdownMenuProps): JSX.Element {
  const [css] = useStyletron();
  const { currentTheme } = useDexterTheme();
  const c = currentTheme.colors;

  const hoverBackground = "rgba(127,127,127,0.14)";

  const slot = css({
    width: "16px",
    display: "inline-flex",
    justifyContent: "center",
    flexShrink: 0,
  });

  return (
    <StatefulPopover
      placement={placement}
      showArrow={false}
      autoFocus
      returnFocus
      overrides={{
        Body: {
          style: {
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
            border: `1px solid ${c.controlBarBorderColor}`,
            backgroundColor: c.reactFlowBackground,
            zIndex: 1000,
          },
        },
        Inner: { style: { backgroundColor: c.reactFlowBackground } },
      }}
      content={({ close }) => (
        <div
          role="menu"
          className={css({
            display: "flex",
            flexDirection: "column",
            padding: "4px 0",
            minWidth: `${minWidth}px`,
          })}
        >
          {items.map((item) => (
            <React.Fragment key={item.id}>
              {item.dividerBefore && (
                <div
                  role="separator"
                  className={css({
                    height: "1px",
                    margin: "4px 0",
                    backgroundColor: c.controlBarBorderColor,
                  })}
                />
              )}
              <ItemTooltip
                content={item.disabled ? item.disabledReason : item.tooltip}
              >
                <Button
                  id={item.id}
                  data-testid={item.id}
                  type="button"
                  role="menuitem"
                  kind={KIND.tertiary}
                  size={SIZE.compact}
                  disabled={item.disabled}
                  onClick={() => {
                    close();
                    item.onSelect();
                  }}
                  overrides={{
                    BaseButton: {
                      style: ({ $disabled }) => ({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: "10px",
                        width: "100%",
                        paddingTop: "8px",
                        paddingBottom: "8px",
                        paddingLeft: "14px",
                        paddingRight: "14px",
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        backgroundColor: "transparent",
                        color: c.text,
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "20px",
                        textAlign: "left",
                        opacity: $disabled ? 0.45 : 1,
                        cursor: $disabled ? "not-allowed" : "pointer",
                        ":hover": {
                          backgroundColor: $disabled
                            ? "transparent"
                            : hoverBackground,
                        },
                        ":active": {
                          backgroundColor: $disabled
                            ? "transparent"
                            : hoverBackground,
                        },
                        ":focus-visible": {
                          outline: "none",
                          boxShadow: "none",
                          backgroundColor: hoverBackground,
                        },
                        ":disabled": {
                          backgroundColor: "transparent",
                          color: c.text,
                        },
                      }),
                    },
                  }}
                >
                  {item.icon && <span className={slot}>{item.icon}</span>}
                  <span className={css({ flex: 1, whiteSpace: "nowrap" })}>
                    {item.label}
                  </span>
                  {item.hint && (
                    <span
                      className={css({
                        marginLeft: "24px",
                        fontFamily: "monospace",
                        fontSize: "13px",
                        opacity: 0.6,
                      })}
                    >
                      {item.hint}
                    </span>
                  )}
                </Button>
              </ItemTooltip>
            </React.Fragment>
          ))}
        </div>
      )}
    >
      {children}
    </StatefulPopover>
  );
}
